import {
  PROFILE_PAGE,
  CUSTOMIZATION_PAGE,
  LANDING_PAGE_ROUTE,
  DOMAIN_PAGE,
  LANDING_PAGE_SETTING_PAGE,
  BILLING_PAGE,
  PAYMENTS_PAGE,
  MEMBERS_PAGE,
  CRM_PAGE,
  TASKS_CODES,
} from 'src/constants';
import { Task } from 'src/store/user/types';

export default class GettingStartedUtils {
  static MapTaskCodeToRoutePath = (taskCode: string): string => {
    let routePath = '';
    switch (taskCode) {
      case TASKS_CODES.SET_PROFILE:
        routePath = PROFILE_PAGE.path;
        break;
      case TASKS_CODES.CUSTOMIZE_PORTAL:
        routePath = CUSTOMIZATION_PAGE.path;
        break;
      case TASKS_CODES.DESIGN_LANDING_PAGE:
        routePath = LANDING_PAGE_ROUTE;
        break;
      case TASKS_CODES.LINK_LANDING:
        routePath = LANDING_PAGE_SETTING_PAGE.path;
        break;
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN:
        routePath = DOMAIN_PAGE.path;
        break;
      case TASKS_CODES.CONNECT_BANK_ACCOUNT:
        routePath = PAYMENTS_PAGE.path;
        break;
      case TASKS_CODES.SETUP_PAYMENT_METHOD:
        routePath = BILLING_PAGE.path;
        break;
      case TASKS_CODES.INVITE_TEAM:
        routePath = MEMBERS_PAGE.path;
        break;
      case TASKS_CODES.FIRST_CLIENT:
        routePath = CRM_PAGE.path;
        break;
      case TASKS_CODES.READ_PORTAL_GUIDE:
        routePath = TASKS_CODES.READ_PORTAL_GUIDE;
        break;
      default:
        break;
    }
    return routePath;
  };

  static MapTaskCodeToTabTitle = (taskCode: string): string => {
    let actionTitle = '';
    switch (taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN:
        actionTitle = 'Connect a domain';
        break;
      case TASKS_CODES.INVITE_TEAM:
        actionTitle = 'Invite your team';
        break;
      case TASKS_CODES.FIRST_CLIENT:
        actionTitle = 'Create your first client';
        break;
      case TASKS_CODES.CONNECT_BANK_ACCOUNT:
        actionTitle = 'Connect your bank';
        break;
      case TASKS_CODES.LINK_LANDING:
        actionTitle = 'Connect your website';
        break;
      case TASKS_CODES.DESIGN_LANDING_PAGE:
        actionTitle = 'Design website';
        break;
      case TASKS_CODES.CUSTOMIZE_PORTAL:
        actionTitle = 'Customize your portal';
        break;
      default:
        actionTitle = '';
        break;
    }
    return actionTitle;
  };

  static MapTaskCodeToActionTitle(taskCode: string) {
    let actionTitle = '';
    switch (taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN:
        actionTitle = 'Connect a custom domain';
        break;
      case TASKS_CODES.INVITE_TEAM:
        actionTitle = 'Invite your teammates';
        break;
      case TASKS_CODES.FIRST_CLIENT:
        actionTitle = 'Create your first client';
        break;
      case TASKS_CODES.CONNECT_BANK_ACCOUNT:
        actionTitle = 'Connect your bank account';
        break;
      case TASKS_CODES.LINK_LANDING:
        actionTitle = 'Connect your marketing website';
        break;
      case TASKS_CODES.DESIGN_LANDING_PAGE:
        actionTitle = 'Design your marketing website';
        break;
      case TASKS_CODES.CUSTOMIZE_PORTAL:
        actionTitle = 'Customize your portal';
        break;
      case TASKS_CODES.VERIFY_EMAIL:
        actionTitle = 'Verify your email';
        break;
      default:
        actionTitle = '';
        break;
    }
    return actionTitle;
  }

  static MapTaskCodeToCompletedTitle(taskCode: string) {
    let completedTitle = '';
    switch (taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN:
        completedTitle = 'Your custom domain has been connected';
        break;
      case TASKS_CODES.INVITE_TEAM:
        completedTitle = 'Invite more teammates';
        break;
      case TASKS_CODES.FIRST_CLIENT:
        completedTitle = 'Invite more clients';
        break;
      case TASKS_CODES.CONNECT_BANK_ACCOUNT:
        completedTitle = 'Your bank account has been connected';
        break;
      case TASKS_CODES.LINK_LANDING:
        completedTitle = 'Marketing website connection';
        break;
      case TASKS_CODES.DESIGN_LANDING_PAGE:
        completedTitle = 'Make updates to your marketing website';
        break;
      case TASKS_CODES.CUSTOMIZE_PORTAL:
        completedTitle = 'Customize your portal';
        break;
      default:
        completedTitle = '';
        break;
    }
    return completedTitle;
  }

  static MapTaskCodeToCompletedTaskDetails(task: Task) {
    const completedTaskDetails = {
      title: this.MapTaskCodeToCompletedTitle(task.taskCode),
      description: '',
      buttonLabel: '',
    };
    switch (task.taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN: {
        completedTaskDetails.buttonLabel = 'Domain settings';
        completedTaskDetails.description =
          'If you need to connect a new custom domain, you can do that on the domain settings page.';
        break;
      }
      case TASKS_CODES.INVITE_TEAM: {
        completedTaskDetails.buttonLabel = 'Invite team';
        completedTaskDetails.description = `You already have ${task.teamMembersCount ||
          0} ${
          task.teamMembersCount === 1 ? 'member' : 'members'
        } on your team but can add more anytime.`;
        break;
      }
      case TASKS_CODES.FIRST_CLIENT: {
        completedTaskDetails.buttonLabel = 'Go to Clients';
        completedTaskDetails.description = `You already have ${task.clientMembersCount ||
          0} ${
          task.clientMembersCount === 1 ? 'client' : 'clients'
        } in your CRM. You can add more clients anytime. `;
        break;
      }
      case TASKS_CODES.CONNECT_BANK_ACCOUNT: {
        completedTaskDetails.buttonLabel = 'Bank settings';
        completedTaskDetails.description =
          'Your bank account is connected to Portal. When your clients make payments, money is sent to your bank automatically.';
        break;
      }
      case TASKS_CODES.LINK_LANDING: {
        completedTaskDetails.buttonLabel = 'Copy link';
        completedTaskDetails.description =
          "You've copied the link that goes to your portal. Now add a button with this link on your marketing website.";
        break;
      }
      case TASKS_CODES.DESIGN_LANDING_PAGE: {
        completedTaskDetails.buttonLabel = 'Design page';
        completedTaskDetails.description =
          'Design your marketing website by removing our placeholder content and adding your own images and text.';
        break;
      }
      case TASKS_CODES.CUSTOMIZE_PORTAL: {
        completedTaskDetails.buttonLabel = 'Customize portal';
        completedTaskDetails.description =
          'Edit the look and feel of your portal.';
        break;
      }
      default:
        break;
    }
    return completedTaskDetails;
  }

  static MapTaskCodeToTaskDetails(taskCode: string) {
    const taskDetails = {
      title: this.MapTaskCodeToActionTitle(taskCode),
      description: '',
      buttonLabel: '',
    };
    switch (taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN: {
        taskDetails.buttonLabel = 'Domain settings';
        taskDetails.description =
          'Connect your custom domain so that your portal is accessible on your own website.';
        break;
      }
      case TASKS_CODES.INVITE_TEAM: {
        taskDetails.buttonLabel = 'Invite team';
        taskDetails.description = 'Invite your team to your portal.';
        break;
      }
      case TASKS_CODES.FIRST_CLIENT: {
        taskDetails.buttonLabel = `Go to Clients`;
        taskDetails.description =
          'Before inviting real clients, invite yourself as a test client to see what the client experience is like.';
        break;
      }
      case TASKS_CODES.CONNECT_BANK_ACCOUNT: {
        taskDetails.buttonLabel = 'Bank settings';
        taskDetails.description =
          'Connect your bank account so that we can send you money when clients pay you.';
        break;
      }
      case TASKS_CODES.LINK_LANDING: {
        taskDetails.buttonLabel = 'Copy link';
        taskDetails.description =
          'On your marketing website, add a button that links to your portal. Most companies name the button "Client portal".';
        break;
      }
      case TASKS_CODES.DESIGN_LANDING_PAGE: {
        taskDetails.buttonLabel = 'Design page';
        taskDetails.description =
          'Design your marketing website by removing our placeholder content and adding your own images and text.';
        break;
      }
      case TASKS_CODES.CUSTOMIZE_PORTAL: {
        taskDetails.buttonLabel = 'Customize portal';
        taskDetails.description = 'Edit the look and feel of your portal.';
        break;
      }
      default:
        break;
    }
    return taskDetails;
  }

  static MapTaskCodeToIllustrationName = (taskCode: string): string => {
    let illustrationName = '';
    switch (taskCode) {
      case TASKS_CODES.CONNECT_CUSTOM_DOMAIN:
        illustrationName = 'connect_custom_domain';
        break;
      case TASKS_CODES.INVITE_TEAM:
        illustrationName = 'invite_your_teammates';
        break;
      case TASKS_CODES.FIRST_CLIENT:
        illustrationName = 'add_first_client';
        break;
      case TASKS_CODES.CONNECT_BANK_ACCOUNT:
        illustrationName = 'connect_bank_account';
        break;
      case TASKS_CODES.LINK_LANDING:
        illustrationName = 'connect_your_website';
        break;
      case TASKS_CODES.DESIGN_LANDING_PAGE:
        illustrationName = 'design_landing_page';
        break;
      case TASKS_CODES.CUSTOMIZE_PORTAL:
        illustrationName = 'customize_your_portal';
        break;
      case TASKS_CODES.VERIFY_EMAIL:
        illustrationName = 'verify_email';
        break;
      default:
        illustrationName = '';
        break;
    }
    return illustrationName;
  };
}
